import React, { forwardRef, useRef, useImperativeHandle, useCallback, useState } from "react";
import { toast } from 'react-toastify';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import ApiClient from "../../../services/api-client"
import ActionsRenderer from '../aggrid-cell-renderers/actions-renderer'
import DoubleValRenderer from "../aggrid-cell-renderers/doubleval-renderer";
import CheckboxRenderer from "../aggrid-cell-renderers/checkbox-renderer";
import SimpleObjectRenderer from "../aggrid-cell-renderers/simple-object-renderer";
import InvoiceTypeEditor from "../aggrid-cell-editors/invoice-type-editor";
import IncomingClassCategoryTypeEditor from "../aggrid-cell-editors/incoming-classification-category-editor";
import IncomingClassValueEditor from "../aggrid-cell-editors/incoming-classification-value-editor";
import SelfPricingRenderer from "../aggrid-cell-renderers/selfpricing-renderer";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = {
    classificationValue: { value: undefined, label: undefined },
    classificationCategory: { value: undefined, label: undefined },
    amount: undefined,
    percent: false
};

const ProductClassificationsGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const gridApiRef = useRef(null);
    const [duplicateError, setDuplicateError] = useState("");

    const isValid = useCallback((data) => {
        if (!data.classificationCategory?.value || !data.classificationValue?.value) {
            return false;
        }
        if (data.invoiceType?.value && gridApiRef.current) {
            let duplicates = 0;
            gridApiRef.current.forEachNode(node => {
                if (node.data !== data && node.data.invoiceType?.value === data.invoiceType.value) {
                    duplicates++;
                }
            });
            if (duplicates > 0) return false;
        }
        return true;
    }, []);

    const { hasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems, deleteRowLocally } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
        globalRows: true,
    });

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
        initGrid(params, props.productClassifications);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            addRow(params.api);
        }

        if (params.column !== undefined) {
            const colid = params.column.colId;
            const rowNode = params.node;

            switch (colid) {
                case "invoiceType": {
                    if (!params.oldValue || !params.oldValue.value) {
                        rowNode.data.percent = true;
                        rowNode.data.amount = 100;
                        params.api.refreshCells({ rowNodes: [rowNode], columns: ['percent', 'amount'], force: true });
                    }
                    break;
                }
            }
        }

        checkErrors(params.api);
        params.api.redrawRows();

        const rows = [];
        params.api.forEachNode(node => rows.push(node.data));
        rows.pop();
        const invoiceTypeValues = rows.map(r => r.invoiceType?.value).filter(Boolean);
        const hasDuplicates = invoiceTypeValues.length !== new Set(invoiceTypeValues).size;
        setDuplicateError(hasDuplicates ? t("duplicateInvoiceTypeCategoryValue") : "");
    };

    useImperativeHandle(ref, () => ({
        hasError() {
            return hasError;
        },
        getData() {
            return getItems().map(element => ({
                id: element.id,
                invoiceType: { id: element.invoiceType.value, myDataInvoiceType: element.invoiceType.myDataInvoiceType, name: element.invoiceType.label },
                classificationCategory: element.classificationCategory.value,
                classificationValue: element.classificationValue.value,
                selfPricing: element.selfPricing ? element.selfPricing : false,
                amount: element.amount,
                percent: element.percent ? element.percent : false,
            }));
        },
        setData(data) {
            setItems(data);
        }
    }));

    const deleteRow = (params) => {
        const rowData = [];
        const id = params.data.id;
        params.api.forEachNode(node => rowData.push(node.data));

        if (id != undefined) {
            ApiClient.deleteProductClassification(id).then(
                () => {
                    rowData.splice(params.rowIndex, 1);
                    params.api.setRowData(rowData);
                    toast.success("deleteSucceded");
                },
                error => {
                    toast.error("Error: " + error.response.data.message);
                    console.error(error.response);
                }
            );
        } else {
            deleteRowLocally(params);
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <span className="row-error-info">{duplicateError}</span>
                <div className="ag-theme-alpine editableSmGrid classificationGrid">
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        onCellValueChanged={onCellValueChanged}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        suppressRowTransform={true}
                        singleClickEdit={true}
                        skipHeaderOnAutoSize={true}
                        suppressRowHoverHighlight={true}
                        defaultColDef={{
                            flex: 1,
                            resizable: false,
                            sortable: false,
                            wrapText: true,
                            suppressMovable: true,
                        }}
                        columnDefs={[
                            { field: "classificationCategoryTypes", hide: true },
                            { field: "classificationValues", hide: true },
                            { field: "invoiceType", flex: 2, minWidth: 200, headerName: t("InvoiceType"), editable: true, cellRendererParams: { options: props.companyInvoiceTypes }, cellEditor: InvoiceTypeEditor, cellRenderer: SimpleObjectRenderer },
                            { field: "selfPricing", flex: 1, minWidth: 150, headerName: t("selfPricing"), editable: false, cellRenderer: SelfPricingRenderer },
                            { field: "classificationCategory", flex: 3, minWidth: 200, headerName: t("incomingClassificationCategoryType"), headerClass: "required", editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: IncomingClassCategoryTypeEditor },
                            { field: "classificationValue", flex: 2, minWidth: 250, headerName: t("incomingClassificationValueType"), editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: IncomingClassValueEditor },
                            { field: "percent", hide: true, flex: 1, minWidth: 150, headerName: t("percent"), cellRenderer: CheckboxRenderer, headerComponentParams: { template: "<span><i class='fa fa-info-circle percentInfoIcon'></i>" + t("percent") + "</span>" } },
                            { field: "amount", hide: true, flex: 1, minWidth: 100, headerName: t("Amount"), editable: true, cellRenderer: DoubleValRenderer },
                            { field: "action", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRow: deleteRow } }
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
            <div><br /></div>
        </div>
    );
});
export default ProductClassificationsGrid;
